import { Mail, Phone, Shield, MapPin, Star } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { memberRoleLabel, roleBadgeVariant, statusLabel } from '@/lib/member-display'
import { COMMITTEE_POSITION_LABELS } from '@riverpark/types'
import type { MemberDetail } from '@riverpark/types'
import { ProfileDetailRow } from './profile-detail-row'
import { getInitials } from './profile-utils'

interface ProfileIdentityCardProps {
  profile: MemberDetail
}

export function ProfileIdentityCard({ profile }: ProfileIdentityCardProps) {
  const initials = getInitials(profile.name)
  const badgeVariant = roleBadgeVariant(profile.role)
  const statusBadgeVariant =
    profile.status === 'active' ? 'paid' : profile.status === 'invited' ? 'invited' : 'draft'

  return (
    <Card className="overflow-hidden">
      <div className="relative h-28 bg-navy">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: `
              linear-gradient(hsl(var(--cream)) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--cream)) 1px, transparent 1px)
            `,
            backgroundSize: '24px 24px',
          }}
          aria-hidden
        />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card to-transparent" />
        {profile.status === 'active' && (
          <span className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-green-lt/40 bg-green-pale px-2.5 py-1 font-sans text-[11px] font-medium text-status-paid-text">
            <span className="size-1.5 rounded-full bg-status-paid-text" aria-hidden />
            Verified
          </span>
        )}
      </div>

      <div className="relative px-6 pb-6">
        <div className="-mt-14 mb-4">
          <div
            className="flex size-[104px] items-center justify-center rounded-full border-[3px] border-card bg-navy-2 ring-1 ring-border-2"
            aria-hidden
          >
            <span className="font-serif text-4xl font-medium tracking-tight text-cream">
              {initials}
            </span>
          </div>
        </div>

        <h2 className="font-serif text-2xl font-medium tracking-tight text-ink">{profile.name}</h2>
        <p className="mt-1 font-sans text-sm text-muted-foreground">Riverpark Estate</p>

        <div className="mt-4 flex flex-wrap gap-2">
          <Badge variant={badgeVariant}>{memberRoleLabel(profile.role, profile.plots)}</Badge>
          {profile.committeeAppointment && (
            <Badge variant="committee">
              {COMMITTEE_POSITION_LABELS[profile.committeeAppointment.position]}
            </Badge>
          )}
          <Badge variant={statusBadgeVariant}>{statusLabel(profile.status)}</Badge>
        </div>

        <div className="mt-6 divide-y divide-border-2 overflow-hidden rounded border border-border-2 bg-cream-2/30">
          {profile.email ? (
            <ProfileDetailRow
              icon={Mail}
              label="Email"
              value={profile.email}
              href={`mailto:${profile.email}`}
            />
          ) : (
            <ProfileDetailRow icon={Mail} label="Email" value="Not provided" />
          )}
          <ProfileDetailRow
            icon={Phone}
            label="Phone"
            value={profile.phone}
            href={`tel:${profile.phone}`}
          />
          <ProfileDetailRow
            icon={Shield}
            label="Account role"
            value={memberRoleLabel(profile.role, profile.plots)}
          />
          {profile.committeeAppointment && (
            <ProfileDetailRow
              icon={Star}
              label="Committee position"
              value={COMMITTEE_POSITION_LABELS[profile.committeeAppointment.position]}
            />
          )}
          {profile.plots.length > 0 && (
            <ProfileDetailRow
              icon={MapPin}
              label="Primary plot"
              value={`${profile.plots[0]!.newNumber} (${profile.plots[0]!.oldNumber})`}
            />
          )}
        </div>
      </div>
    </Card>
  )
}
